<!--
 * @Description: 级联组件二次封装
 * @Author: mdb
 * @Date: 2022-06-28 11:37:34
 * @LastEditTime: 2022-09-15 11:39:31
 * @LastEditors: mdb
-->
<template>
  <CosInputView v-bind="$attrs" :value="currentTxt" v-if="view" />
  <a-cascader v-bind="$attrs" v-model:value="current" showSearch @change="change" change-on-select v-else />
</template>

<script setup lang="ts">
import { ref, watch, useAttrs, toRaw, computed, nextTick } from 'vue'
import { every as lodashEvery, debounce } from 'lodash-es'
import { getTreeIds, getTreeNameList } from '@/utils/index'
import CosInputView from '@/components/Antd/CosView/input.vue'

const attrs = useAttrs()

const props = defineProps<{
  value?: string | number | string[] | number[]
  ids?: string[] | number[]
  type?: 'search'
  view?: boolean
}>()
const current = ref<(number | string)[]>([])

// 监听并回显数据（需要添加函数防抖）
const valueFormat = debounce((value: any, options: any) => {
  // console.log('CosCascader--value和options更新,已防抖：', value, options)
  if (!value || !options) return (current.value = [])
  // 判断是否多选，多选 value 为数组，单选为字符串或数字
  const isMultiple = Array.isArray(value)
  if (isMultiple) {
    const result = value.reduce((arr: any[], cur: any[]) => {
      arr.push(getTreeIds(options, cur))
      return arr
    }, [])
    current.value = result
  } else {
    current.value = getTreeIds(options, value)
  }
}, 200)
watch(
  () => [props.value, props.ids, attrs.options],
  (newValue, oldValue) => {
    const [value, ids, options] = newValue
    if (props.type === 'search') {
      if (ids == null) {
        current.value = []
      } else {
        valueFormat(ids, options)
      }
    } else {
      valueFormat(value, options)
    }
  },
  { immediate: true, deep: true }
)

const currentTxt = computed(() => {
  if (props.value && attrs.options) {
    const labelList = getTreeNameList(attrs.options, props.value, attrs.fieldNames)
    return labelList.join('/') || '--'
  }
  return props.value || '--'
})

const emit = defineEmits(['update:value', 'update:label', 'update:ids', 'change'])
const change = (value: any, selectedOptions: any) => {
  // 1、当 value 不存在时直接返回 undefined
  // 2、或者当 value 为数组且为空数组时，则直接返回 undefined
  if (value == null || (Array.isArray(value) && value.length === 0)) {
    emit('update:value', undefined)
    emit('update:label', undefined)
    emit('update:ids', undefined)
    emit('change', undefined)
    return
  }
  // 判断是否多选，antd 级联多选返回二维数组，单选返回一维数组
  const isMultiple = lodashEvery(value, (item) => Array.isArray(item))
  if (isMultiple) {
    let result = []
    let searchResult = []
    if (props.type === 'search') {
      const lastSelectedOptionList = selectedOptions.map((item: any) => toRaw([item[item.length - 1]]))
      searchResult = lastSelectedOptionList.reduce((arr: any[], current: any[]) => arr.concat(getIds(current)), [])
      emit('update:ids', searchResult)
    } else {
      result = value.reduce((arr: any[], current: any[]) => arr.concat(current[current.length - 1]), [])
      emit('update:value', result)
    }
    emit('change', { custom: props.type === 'search' ? searchResult : result, source: value })
  } else {
    const lastValue = value[value.length - 1]
    if (props.type === 'search') {
      const lastSelectedOption = selectedOptions[selectedOptions.length - 1]
      emit('update:ids', getIds([lastSelectedOption]))
    } else {
      emit('update:value', lastValue)
    }
    emit('change', { custom: lastValue, source: value })
    nextTick(() => {
      emit('update:label', currentTxt.value)
    })
  }
}

// 获取所有 id
const getIds = (data: any[] = [], arr: (string | number)[] = []) => {
  data.forEach((item: any) => {
    arr.push(item.id)
    if (item.children && item.children.length) {
      getIds(item.children, arr)
    }
  })
  return arr
}
</script>
<script lang="ts">
export default { name: 'CosCascader' }
</script>

<style lang="scss" scoped></style>
